// Article covers — real Richmond signage photos
const COVER = {
  monument:     "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/ncbdggkp_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_15_47%20PM.png",
  permit:       "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/x868dejj_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_15_55%20PM.png",
  carytown:     "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/4k4b8gmi_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_16_04%20PM.png",
  neon:         "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/gdpfmu0l_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_16_14%20PM.png",
  westland:     "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/fo3t919j_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_16_23%20PM.png",
  hotel:        "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/vke4f6v5_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_16_32%20PM.png",
  shortPump:    "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/45vcu9qa_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_16_55%20PM.png",
  village:      "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/b5jq4119_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2001_17_02%20PM.png",
  proposal:     "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/yzxftii2_ChatGPT%20Image%20Jun%2013%2C%202026%2C%2012_52_18%20PM.png",
};

export const ARTICLES = [
  {
    slug: "storefront-signs",
    title: "I Need a Sign. Where Do I Start?",
    excerpt: "If someone tells me they need a sign, this is usually where the conversation starts.",
    readTime: "4 min read",
    category: "Sign Types",
    coverImage: COVER.village,
    body: [
      { type: "p", text: "If someone tells me they need a sign, this is usually where the conversation starts." },
      { type: "p", text: "For many businesses, the storefront sign is the first thing customers see. Whether you are opening a new location, moving into a shopping center, or replacing an old sign, it is often the most important sign on the property." },
      { type: "p", text: "The challenge is that “storefront sign” can mean a lot of different things." },
      { type: "p", text: "Some businesses use illuminated letters. Others use solid dimensional letters. Some use cabinet signs. Some combine wall signage with window graphics and door vinyl." },
      { type: "p", text: "The right choice depends on the building, the landlord's requirements, your budget, and how visible you need the sign to be." },

      { type: "h2", text: "What is a storefront sign?" },
      { type: "p", text: "A storefront sign is typically the main sign mounted on the front of a business. Its job is simple: help customers find you." },
      { type: "p", text: "That sounds obvious, but you'd be surprised how often sign decisions get complicated by landlord requirements, permit questions, construction schedules, and budget considerations." },

      { type: "h2", text: "Where you see them" },
      { type: "p", text: "Almost everywhere." },
      { type: "ul", items: [
        "Retail stores",
        "Restaurants",
        "Medical offices",
        "Professional offices",
        "Gyms",
        "Churches",
        "Shopping centers",
        "Industrial buildings",
      ]},
      { type: "p", text: "If customers need to find your location, there is probably a storefront sign involved." },

      { type: "h2", text: "What to know before getting one" },
      { type: "p", text: "The first thing I usually ask is: do you have landlord sign criteria?" },
      { type: "p", text: "Many shopping centers and office parks have rules covering sign size, colors, materials, illumination, and placement. If those requirements exist, it is important to understand them before design work begins." },
      { type: "p", text: "I also want to know a few practical things about the space:" },
      { type: "ul", items: [
        "Is the space already occupied?",
        "Is there an existing sign?",
        "Is power available?",
        "Is the sign replacing something or starting from scratch?",
      ]},
      { type: "p", text: "Those answers help determine the best path forward." },

      { type: "h2", text: "What the process usually looks like" },
      { type: "p", text: "Every project is a little different, but most follow a similar path:" },
      { type: "ul", items: [
        "Gather information about the building and location",
        "Review any landlord requirements",
        "Develop a sign concept",
        "Determine whether permits are required",
        "Fabricate the sign",
        "Schedule installation",
      ]},
      { type: "p", text: "Some projects move quickly. Others require multiple approvals before fabrication can begin." },

      { type: "h2", text: "Final thought" },
      { type: "p", text: "Most people only buy storefront signs a handful of times. The people designing, permitting, fabricating, and installing them deal with them every day." },
      { type: "p", text: "If you are trying to figure out what kind of storefront sign makes sense for your location, give me a call. If I can help, I will. If I can't, I'll point you in the right direction." },
      { type: "cta" },
    ],
  },
  {
    slug: "monument-signs",
    title: "Do I Need a Monument Sign?",
    excerpt: "Sometimes the most important sign is not on the building. It is the one out by the road.",
    readTime: "4 min read",
    category: "Sign Types",
    coverImage: COVER.monument,
    body: [
      { type: "p", text: "When most people think of a business sign, they are usually thinking about the sign on the building." },
      { type: "p", text: "But sometimes the most important sign is not attached to the building at all. It is the sign out by the road. That's what a monument sign is." },

      { type: "h2", text: "What is a monument sign?" },
      { type: "p", text: "A monument sign is a freestanding sign typically located near the entrance of a property. You see them at:" },
      { type: "ul", items: [
        "Shopping centers",
        "Medical offices",
        "Business parks",
        "Churches",
        "Apartment communities",
        "Schools",
        "Industrial facilities",
      ]},
      { type: "p", text: "Their job is simple: help people find the property. In many cases, a monument sign is the first thing a customer sees." },

      { type: "h2", text: "Why choose a monument sign?" },
      { type: "p", text: "The biggest reason is visibility. If your building sits far back from the road, a monument sign can make it much easier for customers, visitors, and deliveries to find you." },
      { type: "p", text: "They can also help identify multiple businesses on the same property. Shopping centers and office parks often use monument signs to display tenant names and directions." },

      { type: "h2", text: "What to know before getting one" },
      { type: "p", text: "Monument signs tend to involve more planning than people expect. The biggest questions are usually:" },
      { type: "ul", items: [
        "Is there already a sign on the property?",
        "Is the existing sign being updated or replaced?",
        "Does the property owner need to approve it?",
        "Are permits required?",
        "How much room is available on the site?",
      ]},
      { type: "p", text: "Every property is different, which is why the first step is usually gathering information about the location." },

      { type: "h2", text: "What the process usually looks like" },
      { type: "p", text: "Most monument sign projects begin with a site review. From there, the process typically includes:" },
      { type: "ul", items: [
        "Reviewing the location",
        "Understanding any property requirements",
        "Developing a design",
        "Determining permit requirements",
        "Fabrication",
        "Installation",
      ]},
      { type: "p", text: "Some projects move quickly. Others require approvals before work can begin." },

      { type: "h2", text: "Final thought" },
      { type: "p", text: "One of the most common mistakes I see is treating a monument sign like an afterthought. In reality, it may be the first thing people see when they visit your business." },
      { type: "p", text: "If you are considering a monument sign and are not sure what your options are, give me a call. If I can help, I will. If I can't, I'll point you in the right direction." },
      { type: "cta" },
    ],
  },
  {
    slug: "building-signs",
    title: "I Need a Sign on My Building. What Should I Know?",
    excerpt: "Often one of the last pieces needed before a business feels complete. Also one of the first things customers notice.",
    readTime: "4 min read",
    category: "Sign Types",
    coverImage: COVER.hotel,
    body: [
      { type: "p", text: "One of the most common calls I get starts with a simple statement: “I need a sign on my building.”" },
      { type: "p", text: "Whether you are opening a new location, replacing an existing sign, or moving into a space for the first time, a building sign is often one of the last pieces needed before a business feels complete. It is also one of the first things customers notice." },

      { type: "h2", text: "What is a building sign?" },
      { type: "p", text: "A building sign is any permanent sign attached directly to a building. Depending on the property, it might be installed above the entrance, on a storefront, over a canopy, or on the side of a building facing a road." },
      { type: "p", text: "The goal is simple: help people find you." },

      { type: "h2", text: "Where you see them" },
      { type: "p", text: "Almost everywhere. You'll see them on:" },
      { type: "ul", items: [
        "Retail stores",
        "Restaurants",
        "Medical offices",
        "Professional offices",
        "Churches",
        "Industrial buildings",
        "Warehouses",
        "Business parks",
      ]},
      { type: "p", text: "If customers, clients, patients, or visitors need to locate a business, there is usually a building sign involved." },

      { type: "h2", text: "What to know before getting one" },
      { type: "p", text: "The first question is usually not what type of sign you want. The first question is whether there are any requirements that need to be followed." },
      { type: "p", text: "Many properties have rules covering:" },
      { type: "ul", items: [
        "Sign size",
        "Placement",
        "Colors",
        "Illumination",
        "Materials",
      ]},
      { type: "p", text: "The second question is visibility. Where are people approaching from? How far away will they be when they first see the building? What needs to be visible from the road? The answers help determine what makes sense for the location." },

      { type: "h2", text: "What the process usually looks like" },
      { type: "p", text: "Most projects start with gathering information about the building. That may include photos, measurements, existing signage, landlord requirements, and visibility considerations." },
      { type: "p", text: "From there, a design is developed, approvals are obtained if necessary, and the project moves into production and installation. Every project is different, but the process is usually more straightforward when the requirements are understood early." },

      { type: "h2", text: "Final thought" },
      { type: "p", text: "Most people spend a lot of time thinking about the inside of their business and very little time thinking about how customers find it. A good building sign helps bridge that gap." },
      { type: "p", text: "If you are considering signage for a new location or replacing an existing sign, I'd be happy to talk through the options. If I can help, I will. If I can't, I'll point you in the right direction." },
      { type: "cta" },
    ],
  },
  {
    slug: "window-graphics",
    title: "Should I Add Graphics to My Windows?",
    excerpt: "Not every sign needs to be mounted to a building. Sometimes the glass is the best place to start.",
    readTime: "4 min read",
    category: "Sign Types",
    coverImage: COVER.carytown,
    body: [
      { type: "p", text: "Not every sign needs to be mounted to a building. Sometimes the glass is the best place to start." },
      { type: "p", text: "Window graphics are one of the most flexible and cost-effective ways to add branding, information, privacy, or advertising to a business. They are also one of the most overlooked." },

      { type: "h2", text: "What are window graphics?" },
      { type: "p", text: "Window graphics are vinyl graphics applied directly to glass. They can be as simple as a business name and hours on a front door, or as large as a full storefront graphic covering multiple windows." },
      { type: "p", text: "Some businesses use them for branding. Others use them for privacy. Some use them to advertise products, services, promotions, or upcoming openings." },

      { type: "h2", text: "Where you see them" },
      { type: "p", text: "Almost everywhere. You'll find them on:" },
      { type: "ul", items: [
        "Retail storefronts",
        "Restaurants",
        "Medical offices",
        "Fitness studios",
        "Churches",
        "Salons",
        "Professional offices",
        "Temporary locations",
      ]},
      { type: "p", text: "If your business has glass, there is a good chance window graphics are an option." },

      { type: "h2", text: "Why choose window graphics" },
      { type: "p", text: "One of the biggest advantages is visibility. Many businesses have large areas of unused glass facing parking lots, sidewalks, or busy roads. Window graphics can help communicate who you are and what you do before a customer ever walks through the door." },
      { type: "p", text: "They can also help create privacy without completely blocking natural light." },
      { type: "p", text: "For new businesses, they can be a great way to establish a presence while larger sign projects are moving through approvals and production." },

      { type: "h2", text: "What to know before getting them" },
      { type: "p", text: "The biggest question is usually how much of the glass should be covered. Sometimes less is more. A simple logo, business name, and hours may be all that is needed. Other times, a larger graphic can help make a business easier to identify from a distance." },
      { type: "p", text: "It is also important to consider how the space functions from the inside. A design that looks great from the parking lot should still work for the people inside the building." },

      { type: "h2", text: "What the process usually looks like" },
      { type: "p", text: "Most projects begin with photos and measurements. From there, a layout is developed and reviewed before production begins. Once approved, the graphics are printed, prepared, and installed on the glass." },
      { type: "p", text: "Many window graphic projects can be completed relatively quickly compared to larger signage projects." },

      { type: "h2", text: "Final thought" },
      { type: "p", text: "If you are opening a business, updating a storefront, or simply looking for a way to make better use of your windows, graphics are often worth considering. They are versatile, highly visible, and can make a big difference in how a business presents itself." },
      { type: "p", text: "If you'd like to explore your options, feel free to reach out. If I can help, I will. If I can't, I'll point you in the right direction." },
      { type: "cta" },
    ],
  },
  {
    slug: "do-i-need-a-sign-permit",
    title: "Do I Need a Sign Permit?",
    excerpt: "Almost always, yes. Here is the plain-English version of what that means and who reviews it.",
    readTime: "4 min read",
    category: "Permitting",
    coverImage: COVER.permit,
    body: [
      { type: "p", text: "Short answer: almost always, yes." },
      { type: "p", text: "In Central Virginia, just about every commercial sign needs a permit. The exceptions are small — temporary signs, a real estate banner during construction, small window vinyl. If you are putting up anything permanent that customers will see from the road, plan on a permit." },
      { type: "p", text: "The trickier part is not whether you need one. It is figuring out who reviews it." },
      { type: "p", text: "Richmond is a patchwork. The City of Richmond handles its own permits. Henrico, Chesterfield, and Hanover counties each run their own process. Ashland has a separate review from the rest of Hanover. If your sign sits anywhere near a state highway, the Virginia Department of Transportation gets involved too." },
      { type: "p", text: "Then there is your landlord. If you are leasing the space, the landlord almost always has to sign off before anything goes to the city or county. People forget that step all the time." },
      { type: "h2", text: "Two things that surprise people" },
      { type: "p", text: "The landlord approval has to happen first. You cannot just walk into the county office. Most jurisdictions want a letter from your landlord confirming they have approved the design." },
      { type: "p", text: "If you are in an older part of Richmond — Carytown, the Fan, Church Hill, places with historic overlays — there is an extra review on top of the regular sign permit. It adds weeks. It is not a deal-breaker, but you want to know about it on day one, not day fifty." },
      { type: "p", text: "If you are not sure which jurisdiction governs your sign, send me the address. I can usually tell you within an hour." },
      { type: "cta" },
    ],
  },
  {
    slug: "landlord-rejected-sign-package",
    title: "Why Did My Landlord Reject My Sign Package?",
    excerpt: "The handful of reasons this almost always happens, and how to fix it.",
    readTime: "5 min read",
    category: "Landlord Approvals",
    coverImage: COVER.shortPump,
    body: [
      { type: "p", text: "Few things are more frustrating than waiting three weeks on your landlord and then getting an email saying the sign package was rejected. Especially when you are paying rent and the build-out clock is ticking." },
      { type: "p", text: "Here is the thing — most rejections happen for the same handful of reasons. Once you know what landlords are looking for, the package is easier to get through the first time." },
      { type: "h2", text: "Start with the document most tenants never read" },
      { type: "p", text: "Almost every commercial lease has a sign criteria document attached. It is a separate document from the lease itself, and a lot of tenants never open it. That document spells out exactly what is allowed: how tall the letters can be, what kind of lighting, what materials, what colors, where on the storefront the sign can go. If your design does not match what is in that document, it gets bounced." },
      { type: "h2", text: "The most common rejection reasons" },
      { type: "p", text: "Letters that are too tall. The wrong kind of lighting. A sign that covers too much of the storefront. A logo that is bigger than the words. A color that does not match the brand the landlord has on file. Materials that do not fit the rest of the property. A sign placed outside the area the landlord designated for signage." },
      { type: "p", text: "And honestly — sometimes it is just that the package was incomplete. Landlords expect to see the storefront drawn to scale, the sign drawn in place, a note about materials and colors, and a clear explanation of how it will be installed and lit. If half of that is missing, the package comes back." },
      { type: "p", text: "If your package was rejected and you are not sure what to fix, send me the rejection email and your drawings. I can usually point to the conflict in a few minutes." },
      { type: "cta" },
    ],
  },
  {
    slug: "repair-or-replace",
    title: "Should I Repair My Sign or Replace It?",
    excerpt: "How to decide whether to fix the sign you have or start over.",
    readTime: "4 min read",
    category: "Maintenance",
    coverImage: COVER.neon,
    body: [
      { type: "p", text: "Signs do not last forever. Lights dim, the plastic faces yellow, cabinets rust, and one day you look up and your storefront looks tired. The question is whether to fix what you have or start over." },
      { type: "h2", text: "When repair usually makes sense" },
      { type: "p", text: "Repair usually makes sense when the bones of the sign are still good — the structure is solid, the mounting is sound, the brand has not changed — and what is failing is something specific. The lights are out but the face is fine. Or the face is fine but the lights have failed. Or the cabinet is solid but the colors have faded. Fixing those things is almost always cheaper than starting over." },
      { type: "h2", text: "When replacing is the right call" },
      { type: "p", text: "Replace usually makes sense when the underlying sign is past its useful life — the cabinet is rusting or sagging, water is getting inside, the face has cracked or yellowed past what cleaning will fix — or when your brand has changed and the old sign no longer represents you well." },
      { type: "p", text: "Also worth considering: if you have already had the lights swapped out once or twice on the same cabinet, the next round is usually a sign that the whole thing has reached the end." },
      { type: "h2", text: "A middle option people miss" },
      { type: "p", text: "For monument signs especially, the base and the support structure are often still solid even when the face looks rough. Updating the face and the lighting while keeping the existing structure can give you another decade out of the sign for a fraction of what a full replacement costs." },
      { type: "p", text: "Before you decide either way, have somebody actually look at the structure — not just the face. Confirm the wiring is still in good shape. And check whether the zoning rules have changed since the sign went up. Sometimes you can fix an existing sign as-is, but a full replacement triggers updated requirements." },
      { type: "p", text: "Send me a photo of what you have and what is bothering you about it. I can usually tell you within a few minutes whether repair is worth it." },
      { type: "cta" },
    ],
  },
  {
    slug: "what-to-ask-before-signing-a-sign-proposal",
    title: "What To Ask Before Signing a Proposal",
    excerpt: "A short list of plain-English questions worth asking before you sign.",
    readTime: "5 min read",
    category: "Quote Review",
    coverImage: COVER.proposal,
    body: [
      { type: "p", text: "Sign companies are not out to get you. Most are good operators doing honest work. The challenge is that the relationship is one-sided. They handle signs every day. You probably handle them once a decade, if that. Asking a few specific questions levels the table." },
      { type: "h2", text: "About what is included" },
      { type: "p", text: "Is the foundation included in this price? Is the electrical included from my building all the way to the sign, or only partway? What permits are covered, and are the fees included or billed separately? Who is getting landlord approval — your team or mine?" },
      { type: "h2", text: "About the timeline" },
      { type: "p", text: "What is the lead time, and what starts that clock? Is it the deposit, the permit, or final drawings? If permits take longer than expected, who absorbs the delay? When does fabrication actually start, and when is installation scheduled?" },
      { type: "h2", text: "About risk" },
      { type: "p", text: "What does the warranty cover — the structure, the lights, the installation? If we need a lift or crane on install day, who pays for it? If something goes wrong with the landlord or the county, what happens to my deposit? If the project scope changes mid-way, what is the rate for those changes? Will you give me paperwork at the end confirming I have paid in full?" },
      { type: "p", text: "If a sign company can answer all of these clearly, you have a good partner. If they cannot, that is worth a pause." },
      { type: "p", text: "And if you would rather not navigate this alone, send me the proposal and I will walk through it with you." },
      { type: "cta" },
    ],
  },
  {
    slug: "after-approval-process",
    title: "What Happens After I Approve a Sign Quote?",
    excerpt: "A plainspoken walk-through of every step between your signature and install day.",
    readTime: "6 min read",
    category: "Project Process",
    coverImage: COVER.westland,
    body: [
      { type: "p", text: "A lot of people think the day you sign the quote is the day fabrication starts. It is not. There is actually a sequence of things that happen between your signature and the truck pulling up. Knowing the steps in advance helps with managing expectations, especially around timing." },
      { type: "h2", text: "What actually happens, in order" },
      { type: "p", text: "First, the sign company finalizes the drawings. For bigger signs this includes structural engineering. Smaller signs skip that step." },
      { type: "p", text: "Then your landlord has to approve the final design. If they signed off earlier on the concept, this part is usually quick. If they have not seen it yet, expect a few weeks. Bigger property management companies move slower than smaller ones." },
      { type: "p", text: "After landlord approval, the drawings go to the city or county for the sign permit. Wall-mounted signs usually come back faster than monument signs. Anything in a historic district takes longer." },
      { type: "p", text: "Once the permit is in hand, the sign actually gets built. Lead time depends on the type of sign, the materials, and how busy the shop is." },
      { type: "p", text: "While that is happening, site work gets done. Power is run to the sign location. Concrete is poured for monument signs. Any old sign that is coming down gets removed." },
      { type: "p", text: "Then installation day — usually one to three days depending on the sign. Wall signs typically install in a day. Monument signs take longer because the concrete has to cure." },
      { type: "p", text: "Finally, the city or county inspects the installation. Once it passes, you get the final paperwork and warranty documents." },
      { type: "h2", text: "How long does the whole thing take?" },
      { type: "p", text: "For most wall signs in the Richmond market, plan on a couple of months from approval to install. Monument signs usually take longer. If you are in a historic district or near a state highway, plan on longer still." },
      { type: "p", text: "The biggest thing to know: most of the wait is not the sign company moving slowly. It is the landlord and the permit office. Building that into your timeline early saves a lot of frustration." },
      { type: "cta" },
    ],
  },
];

export function getArticle(slug) {
  return ARTICLES.find(a => a.slug === slug);
}
